import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  getHelpSectionForPath,
  getLocalizedHelpSections,
} from "../help/helpContent";

function getInitialSectionId() {
  if (typeof window === "undefined") {
    return "quickstart";
  }

  return getHelpSectionForPath(
    window.location.pathname,
    window.location.hash
  );
}

/*
 * Ohjekeskus renderöidään portaalilla document.bodyyn.
 * Siksi se ei välttämättä ole Hallinnan theme-light-elementin sisällä.
 *
 * Etsitään sivulta aktiivinen theme-light-luokka, mutta jätetään
 * ohjekeskuksen oma portaali pois tarkistuksesta.
 */
function documentUsesLightTheme() {
  if (typeof document === "undefined") {
    return false;
  }

  const lightThemeElements = Array.from(
    document.querySelectorAll(".theme-light")
  );

  return lightThemeElements.some(
    (element) => !element.closest("[data-help-center-portal]")
  );
}

export default function HelpCenter() {
  // Painike seuraa yhteistä kielivalintaa. Ohjeartikkelit käännetään erikseen.
  const { t, i18n } = useTranslation("admin");

  const localizedSections = useMemo(
    () => getLocalizedHelpSections(t),
    [t, i18n.resolvedLanguage]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] =
    useState(getInitialSectionId);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLightTheme, setIsLightTheme] = useState(() =>
    documentUsesLightTheme()
  );

  /*
   * Seurataan Hallinnan teeman vaihtumista.
   * Näin avoinna oleva ohjekeskus vaihtaa värit heti ilman sivun päivitystä.
   */
  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    function updateTheme() {
      setIsLightTheme(documentUsesLightTheme());
    }

    updateTheme();

    const observer = new MutationObserver(updateTheme);

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow =
      document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow =
        previousHtmlOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  function openHelp() {
    setActiveSectionId(getInitialSectionId());
    setIsOpen(true);
  }

  const filteredSections = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return localizedSections;
    }

    return localizedSections.filter((section) => {
      const haystack = [
        section.title,
        section.summary,
        ...(section.tags || []),
        ...(section.steps || []),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [localizedSections, searchTerm]);

  const activeSection =
    localizedSections.find(
      (section) => section.id === activeSectionId
    ) ||
    filteredSections[0] ||
    localizedSections[0];

  /*
   * Portaalin värit asetetaan suoraan tunnistetun teeman perusteella.
   * Näin toiminta ei riipu siitä, missä elementissä theme-light sijaitsee.
   */
  const theme = isLightTheme
    ? {
        overlay:
          "bg-[#241a16]/45 text-[#241a16]",
        modal:
          "border-[#5a3824]/20 bg-[#fff8ef] shadow-[0_28px_90px_rgba(58,43,24,0.28)]",
        headerBorder:
          "border-[#5a3824]/15",
        heading:
          "text-[#241a16]",
        bodyText:
          "text-[#5a3824]",
        mutedText:
          "text-[#8a5a35]",
        closeButton:
          "border-[#5a3824]/20 bg-white/70 text-[#541717] hover:bg-white",
        aside:
          "border-[#5a3824]/15 bg-[#f3e5d9]/45",
        searchLabel:
          "text-[#6b4229]",
        searchInput:
          "border-[#5a3824]/20 bg-white text-[#241a16] placeholder:text-[#8a5a35]/65 focus:border-red-500",
        activeNavigation:
          "border-red-400/60 bg-red-500/10 text-[#541717]",
        inactiveNavigation:
          "border-[#5a3824]/15 bg-white/60 text-[#5a3824] hover:border-[#5a3824]/25 hover:bg-white",
        navigationSummary:
          "text-[#8a5a35]",
        emptyState:
          "border-[#5a3824]/15 bg-white/60 text-[#6b4229]",
        stepCard:
          "border-[#5a3824]/15 bg-white/70",
        stepText:
          "text-[#5a3824]",
      }
    : {
        overlay:
          "bg-black/85 text-white",
        modal:
          "border-white/10 bg-zinc-950 shadow-2xl",
        headerBorder:
          "border-white/10",
        heading:
          "text-white",
        bodyText:
          "text-zinc-300",
        mutedText:
          "text-zinc-500",
        closeButton:
          "border-white/10 bg-white/[0.04] text-white hover:bg-white/10",
        aside:
          "border-white/10 bg-transparent",
        searchLabel:
          "text-zinc-500",
        searchInput:
          "border-white/10 bg-black text-white placeholder:text-zinc-600 focus:border-red-400",
        activeNavigation:
          "border-red-400/60 bg-red-500/15 text-white",
        inactiveNavigation:
          "border-white/10 bg-white/[0.03] text-zinc-300 hover:bg-white/[0.06]",
        navigationSummary:
          "text-zinc-500",
        emptyState:
          "border-white/10 bg-white/[0.03] text-zinc-400",
        stepCard:
          "border-white/10 bg-black",
        stepText:
          "text-zinc-200",
      };

  const modal =
    isOpen && typeof document !== "undefined"
      ? createPortal(
          <div
            data-help-center-portal
            className={[
              "fixed inset-0 z-[2147483647] overflow-hidden",
              "p-0 backdrop-blur sm:p-4",
              theme.overlay,
            ].join(" ")}
            role="dialog"
            aria-modal="true"
            aria-label={t("helpCenter.dialogLabel")}
          >
            <div
              className={[
                "mx-auto flex h-[100svh] w-full max-w-6xl",
                "flex-col overflow-hidden rounded-none border",
                "sm:h-[calc(100svh-2rem)] sm:rounded-[2rem]",
                theme.modal,
              ].join(" ")}
            >
              <header
                className={[
                  "flex shrink-0 items-start justify-between",
                  "gap-3 border-b px-4 py-3",
                  "sm:gap-4 sm:px-6 sm:py-4",
                  theme.headerBorder,
                ].join(" ")}
              >
                <div className="min-w-0">
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-red-500">
                    Paranoidi
                  </p>

                  <h2
                    className={[
                      "mt-2 text-2xl font-black uppercase",
                      "tracking-[-0.04em] sm:text-3xl",
                      theme.heading,
                    ].join(" ")}
                  >
                    {t("helpCenter.title")}
                  </h2>

                  <p
                    className={[
                      "mt-2 hidden max-w-2xl text-sm",
                      "font-semibold leading-6 sm:block",
                      theme.mutedText,
                    ].join(" ")}
                  >
                    {t("helpCenter.description")}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className={[
                    "shrink-0 rounded-full border px-4 py-2",
                    "text-xs font-black uppercase tracking-[0.14em]",
                    "transition",
                    theme.closeButton,
                  ].join(" ")}
                >
                  {t("helpCenter.close")}
                </button>
              </header>

              <div className="flex min-h-0 flex-1 flex-col lg:grid lg:grid-cols-[22rem_1fr]">
                <aside
                  className={[
                    "max-h-[42svh] shrink-0 overflow-y-auto",
                    "border-b p-3 sm:max-h-[36svh] sm:p-4",
                    "lg:max-h-none lg:min-h-0 lg:border-b-0",
                    "lg:border-r lg:p-5",
                    theme.aside,
                  ].join(" ")}
                >
                  <label
                    className={[
                      "grid gap-2 text-xs font-black uppercase",
                      "tracking-[0.14em]",
                      theme.searchLabel,
                    ].join(" ")}
                  >
                    {t("helpCenter.searchLabel")}

                    <input
                      type="search"
                      value={searchTerm}
                      onChange={(event) =>
                        setSearchTerm(event.target.value)
                      }
                      className={[
                        "rounded-2xl border px-4 py-3",
                        "text-sm font-bold normal-case",
                        "tracking-normal outline-none transition",
                        theme.searchInput,
                      ].join(" ")}
                      placeholder={t("helpCenter.searchPlaceholder")}
                    />
                  </label>

                  <nav className="mt-4 grid gap-2">
                    {filteredSections.map((section) => (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() =>
                          setActiveSectionId(section.id)
                        }
                        className={[
                          "rounded-2xl border px-4 py-3",
                          "text-left transition",
                          activeSection?.id === section.id
                            ? theme.activeNavigation
                            : theme.inactiveNavigation,
                        ].join(" ")}
                      >
                        <span className="block text-sm font-black">
                          {section.title}
                        </span>

                        <span
                          className={[
                            "mt-1 hidden text-xs font-semibold",
                            "leading-5 sm:block",
                            theme.navigationSummary,
                          ].join(" ")}
                        >
                          {section.summary}
                        </span>
                      </button>
                    ))}

                    {filteredSections.length === 0 && (
                      <p
                        className={[
                          "rounded-2xl border px-4 py-3",
                          "text-sm font-semibold leading-6",
                          theme.emptyState,
                        ].join(" ")}
                      >
                        {t("helpCenter.noResults")}
                      </p>
                    )}
                  </nav>
                </aside>

                <section className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-red-500">
                    {t("helpCenter.sectionLabel")}
                  </p>

                  <h3
                    className={[
                      "mt-3 text-2xl font-black uppercase",
                      "tracking-[-0.05em] sm:text-4xl lg:text-5xl",
                      theme.heading,
                    ].join(" ")}
                  >
                    {activeSection.title}
                  </h3>

                  <p
                    className={[
                      "mt-4 max-w-3xl text-sm font-semibold",
                      "leading-7 sm:text-base sm:leading-8",
                      theme.bodyText,
                    ].join(" ")}
                  >
                    {activeSection.summary}
                  </p>

                  <ol className="mt-7 grid gap-3">
                    {(activeSection.steps || []).map(
                      (step, index) => (
                        <li
                          key={`${activeSection.id}-${index}`}
                          className={[
                            "grid gap-3 rounded-2xl border",
                            "px-4 py-4",
                            "sm:grid-cols-[3rem_1fr]",
                            "sm:items-start",
                            theme.stepCard,
                          ].join(" ")}
                        >
                          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500 text-sm font-black text-white">
                            {index + 1}
                          </span>

                          <span
                            className={[
                              "text-sm font-semibold leading-7",
                              theme.stepText,
                            ].join(" ")}
                          >
                            {step}
                          </span>
                        </li>
                      )
                    )}
                  </ol>
                </section>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  const floatingButton =
    typeof document !== "undefined"
      ? createPortal(
          <div data-help-center-portal>
            <button
              type="button"
              onClick={openHelp}
              style={{
                position: "fixed",
                right:
                  "max(1rem, env(safe-area-inset-right))",
                bottom:
                  "max(1rem, env(safe-area-inset-bottom))",
                zIndex: 2147483000,
              }}
              className={[
                "inline-flex items-center gap-2 rounded-full",
                "border border-red-400/35 bg-red-500",
                "px-4 py-3 text-sm font-black uppercase",
                "tracking-[0.12em] text-white",
                "transition hover:-translate-y-0.5",
                "hover:bg-red-400 active:scale-[0.98] sm:px-5",
                isLightTheme
                  ? "shadow-[0_18px_45px_rgba(84,23,23,0.28)]"
                  : "shadow-[0_18px_60px_rgba(0,0,0,0.55)]",
              ].join(" ")}
              aria-label={t("helpButton.open")}
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-black/25 text-base">
                ?
              </span>

              <span className="hidden sm:inline">{t("helpButton.label")}</span>
            </button>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {floatingButton}
      {modal}
    </>
  );
}